import {
  CopyObjectCommand,
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

import { DriveServiceImpl } from './api';
import type { H2ODriveConfig, S3Object, STSCredentials } from './types';

export class S3Service {
  private s3Client: S3Client | null = null;
  private driveService: DriveServiceImpl | null = null;
  private currentCredentials: STSCredentials | null = null;
  private config: H2ODriveConfig | null = null;
  private credentialRetryCount = 0;
  private readonly MAX_CREDENTIAL_RETRIES = 3;

  async initialize(config: H2ODriveConfig): Promise<void> {
    this.config = config;
    this.driveService = new DriveServiceImpl(config.driveStsUrl, fetch);
    await this.refreshCredentials();
  }

  async listObjects(prefix?: string): Promise<S3Object[]> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();
      const command = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: prefix,
        Delimiter: '/',
      });
      const result = await this.s3Client.send(command);

      const directories = (result.CommonPrefixes || [])
        .map((cp) => cp.Prefix)
        .filter((prefixKey) => {
          if (!prefixKey) return false;

          if (!prefix) {
            const pathWithoutTrailing = prefixKey.slice(0, -1);
            return !pathWithoutTrailing.includes('/');
          }

          const relativePath = prefixKey.substring(prefix.length);
          const pathWithoutTrailing = relativePath.slice(0, -1);
          return pathWithoutTrailing && !pathWithoutTrailing.includes('/');
        })
        .map((prefixKey) => ({
          Key: prefixKey,
          LastModified: undefined,
          Size: undefined,
        }));

      // Process files (Contents)
      const files = (result.Contents || []).filter((obj) => {
        if (!obj.Key) return false;

        // Skip directory markers (objects that end with "/")
        if (obj.Key.endsWith('/')) return false;

        // If no prefix, show all top-level files
        if (!prefix) {
          return !obj.Key.includes('/');
        }

        // For subdirectories, ensure the file is directly in this directory
        const relativePath = obj.Key.substring(prefix.length);
        return relativePath && !relativePath.includes('/');
      });

      // Add parent directory link for non-root directories
      const items = [...directories, ...files];

      if (prefix) {
        // Create a parent directory item that appears first
        const parentItem = {
          Key: '../',
          LastModified: undefined,
          Size: undefined,
          isParentDirectory: true, // Special flag to identify this item
        };
        items.unshift(parentItem); // Add to beginning of array
      }

      return items;
    } catch (error) {
      console.error('Failed to list objects:', error);
      throw error;
    }
  }

  async listAllObjects(prefix?: string): Promise<S3Object[]> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();
      const command = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: prefix,
        // No delimiter - this will return ALL objects recursively
      });
      const result = await this.s3Client.send(command);

      // Process all objects (both files and directories)
      return (result.Contents || []).map((obj) => ({
        Key: obj.Key || '',
        LastModified: obj.LastModified,
        Size: obj.Size,
      }));
    } catch (error) {
      console.error('Failed to list all objects:', error);
      throw error;
    }
  }

  async uploadFile(file: File, key?: string, onProgress?: (progress: number) => void): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      // Convert File to ArrayBuffer for better compatibility
      const fileBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(fileBuffer);

      // Use Upload from @aws-sdk/lib-storage for progress tracking
      const upload = new Upload({
        client: this.s3Client,
        params: {
          Bucket: bucketName,
          Key: key || file.name,
          Body: uint8Array,
          ContentType: file.type,
        },
        // Use 5MB part size (S3 minimum for multipart uploads)
        // Files larger than this will be split into multiple parts
        partSize: 5 * 1024 * 1024, // 5MB
        queueSize: 4, // Upload up to 4 parts concurrently
      });

      // Track upload progress
      if (onProgress) {
        upload.on('httpUploadProgress', (progress: { loaded?: number; total?: number }) => {
          if (progress.loaded !== undefined && progress.total !== undefined) {
            const percentComplete = Math.round((progress.loaded / progress.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      await upload.done();
    } catch (error) {
      // Check if this is a deserialization error but upload actually succeeded (HTTP 200)
      const errorObj = error as any;
      const responseStatus = errorObj?.$response?.statusCode || errorObj?.response?.statusCode;
      const errorMessage = errorObj?.message || '';

      const isDeserializationError =
        errorMessage.includes('Deserialization error') || errorMessage.includes('Expected object, got string');

      const isSuccessStatus = responseStatus === 200;

      if (isDeserializationError && isSuccessStatus) {
        // Upload actually succeeded despite the deserialization error
        console.warn('Upload succeeded but response deserialization failed - this is likely a CORS/proxy issue', {
          key: key || file.name,
          status: responseStatus,
        });
        if (onProgress) onProgress(100);
        return; // Don't throw the error - upload was successful
      }

      // Real error - log and throw
      console.error('Failed to upload file:', error);
      if (error && typeof error === 'object') {
        console.error('Error details:', {
          message: errorMessage,
          name: errorObj?.name,
          status: responseStatus,
        });
      }
      throw error;
    }
  }

  async deleteFile(key: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();
      const command = new DeleteObjectCommand({ Bucket: bucketName, Key: key });
      await this.s3Client.send(command);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  async deleteDirectory(directoryKey: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      // List all objects with this prefix to delete recursively
      const listCommand = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: directoryKey,
      });

      const result = await this.s3Client.send(listCommand);
      const objectsToDelete = result.Contents || [];

      if (objectsToDelete.length === 0) {
        // If no objects found, just try to delete the directory marker
        await this.deleteFile(directoryKey);
        return;
      }

      // Delete all objects in batches (S3 supports up to 1000 objects per batch)
      const batchSize = 1000;
      for (let i = 0; i < objectsToDelete.length; i += batchSize) {
        const batch = objectsToDelete.slice(i, i + batchSize);

        // Delete each object in the batch
        for (const obj of batch) {
          if (obj.Key) {
            await this.deleteFile(obj.Key);
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete directory:', error);
      throw error;
    }
  }

  async createDirectory(directoryName: string, prefix?: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      const normalizedPrefix = prefix ? (prefix.endsWith('/') ? prefix : `${prefix}/`) : '';
      const directoryKey = `${normalizedPrefix}${directoryName}/`;

      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: directoryKey,
        Body: new Uint8Array(0),
        ContentType: 'application/x-directory',
      });

      await this.s3Client.send(command);
    } catch (error) {
      console.error('Failed to create directory:', error);
      throw error;
    }
  }

  async copyFile(sourceKey: string, destinationKey: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      const command = new CopyObjectCommand({
        Bucket: bucketName,
        CopySource: `${bucketName}/${sourceKey}`,
        Key: destinationKey,
      });

      await this.s3Client.send(command);
    } catch (error) {
      console.error('Failed to copy file:', error);
      throw error;
    }
  }

  async copyDirectory(sourceKey: string, destinationKey: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      // List all objects with the source prefix
      const listCommand = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: sourceKey,
      });

      const result = await this.s3Client.send(listCommand);
      const objectsToCopy = result.Contents || [];

      if (objectsToCopy.length === 0) {
        // If no objects found, just copy the directory marker
        await this.copyFile(sourceKey, destinationKey);
        return;
      }

      // Copy each object
      for (const obj of objectsToCopy) {
        if (obj.Key) {
          // Calculate the new key by replacing the source prefix with destination prefix
          const relativePath = obj.Key.substring(sourceKey.length);
          const normalizedDestinationKey = destinationKey.endsWith('/') ? destinationKey : `${destinationKey}/`;
          const newKey = normalizedDestinationKey + relativePath;
          await this.copyFile(obj.Key, newKey);
        }
      }
    } catch (error) {
      console.error('Failed to copy directory:', error);
      throw error;
    }
  }

  async moveFile(sourceKey: string, destinationKey: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      // First copy the file
      await this.copyFile(sourceKey, destinationKey);
      // Then delete the original
      await this.deleteFile(sourceKey);
    } catch (error) {
      console.error('Failed to move file:', error);
      throw error;
    }
  }

  async moveDirectory(sourceKey: string, destinationKey: string): Promise<void> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      // First copy the directory
      await this.copyDirectory(sourceKey, destinationKey);
      // Then delete the original
      await this.deleteDirectory(sourceKey);
    } catch (error) {
      console.error('Failed to move directory:', error);
      throw error;
    }
  }

  async downloadFile(key: string): Promise<Blob> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: key,
      });

      const result = await this.s3Client.send(command);

      if (!result.Body) {
        throw new Error('File not found or empty');
      }

      const arrayBuffer = await result.Body.transformToByteArray();

      return new Blob([arrayBuffer as unknown as ArrayBuffer], {
        type: result.ContentType || 'application/octet-stream',
      });
    } catch (error) {
      console.error('Failed to download file:', error);
      throw error;
    }
  }

  async generatePresignedUrl(key: string, expiresInSeconds = 3600): Promise<string> {
    if (!this.s3Client) {
      throw new Error('S3 client not initialized');
    }

    try {
      await this.ensureValidCredentials();
      const bucketName = this.getBucketName();

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: key,
      });

      return await getSignedUrl(this.s3Client, command, {
        expiresIn: expiresInSeconds,
      });
    } catch (error) {
      console.error('Failed to generate presigned URL:', error);
      throw error;
    }
  }

  private async refreshCredentials(): Promise<void> {
    if (!this.driveService) {
      throw new Error('Drive service not initialized');
    }

    if (this.credentialRetryCount >= this.MAX_CREDENTIAL_RETRIES) {
      const error = new Error(
        `Failed to refresh credentials after ${this.MAX_CREDENTIAL_RETRIES} attempts. Please check your connection and try again.`
      );
      this.credentialRetryCount = 0; // Reset for next attempt
      throw error;
    }

    try {
      this.credentialRetryCount++;
      const response = await this.driveService.getAccessKeys();
      const { credentials } = response;

      if (
        !credentials.accessKeyId ||
        !credentials.secretAccessKey ||
        !credentials.sessionToken ||
        !credentials.expiration
      ) {
        throw new Error('Invalid credentials received from STS');
      }

      this.currentCredentials = {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
        expiration: credentials.expiration,
      };

      if (!this.config?.driveS3Url) {
        throw new Error('Drive S3 URL not configured');
      }

      this.s3Client = new S3Client({
        endpoint: this.config.driveS3Url,
        region: 'us-east-1',
        credentials: {
          accessKeyId: this.currentCredentials.accessKeyId,
          secretAccessKey: this.currentCredentials.secretAccessKey,
          sessionToken: this.currentCredentials.sessionToken,
        },
        forcePathStyle: true,
      });

      // Reset retry count on success
      this.credentialRetryCount = 0;
    } catch (error) {
      console.error('Failed to refresh credentials:', error);
      throw error;
    }
  }

  private async ensureValidCredentials(): Promise<void> {
    if (!this.currentCredentials || !this.driveService) {
      await this.refreshCredentials();
      return;
    }

    if (this.isCredentialsExpired(this.currentCredentials)) {
      await this.refreshCredentials();
    }
  }

  private getBucketName(): string {
    const workspaceId = this.config?.workspaceId || 'default';
    return workspaceId.includes('/') ? workspaceId.split('/').pop() || 'default' : workspaceId;
  }

  private isCredentialsExpired(credentials: STSCredentials): boolean {
    const expirationTime = new Date(credentials.expiration);
    const now = new Date();
    return expirationTime.getTime() - now.getTime() < 5 * 60 * 1000;
  }
}
